/**
 * Domain Switcher component
 * Displays list of websites/domains with ability to switch between them
 */

import { useTranslation } from 'react-i18next';
import { DropdownLabel, DropdownItem, DropdownSeparator } from './Dropdown';
import { Website } from '../../lib/auth';

interface DomainSwitcherProps {
  websites: Website[];
  onSwitchDomain: (websiteId: string) => void;
  onAddWebsite: () => void;
  isLoading?: boolean;
}

export default function DomainSwitcher({
  websites,
  onSwitchDomain,
  onAddWebsite,
  isLoading = false,
}: DomainSwitcherProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
        {t('domainSwitcher.loading', 'Loading websites...')}
      </div>
    );
  }

  if (!websites || websites.length === 0) {
    return (
      <div>
        <DropdownLabel>{t('domainSwitcher.label', 'Websites')}</DropdownLabel>
        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          {t('domainSwitcher.empty', 'No websites yet')}
        </div>
        <DropdownSeparator />
        <DropdownItem onSelect={onAddWebsite}>
          <span className="font-medium">+ {t('domainSwitcher.add', 'Add website')}</span>
        </DropdownItem>
      </div>
    );
  }

  const activeWebsite = websites.find(ws => ws.isActive);
  const primaryWebsite = websites.find(ws => ws.isPrimary);

  return (
    <div>
      <DropdownLabel>{t('domainSwitcher.label', 'Websites')}</DropdownLabel>

      {websites.map((website) => (
        <DropdownItem
          key={website.id}
          onSelect={() => {
            if (!website.isActive) {
              onSwitchDomain(website.id);
            }
          }}
          disabled={website.isActive}
          className={website.isActive ? 'bg-gray-100 dark:bg-gray-800 font-bold' : ''}
        >
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate">{website.name}</span>
              {website.isActive && (
                <span className="text-xs text-green-600 dark:text-green-400 font-bold">
                  ✓
                </span>
              )}
              {website.isPrimary && (
                <span className="text-xs bg-black dark:bg-white text-white dark:text-black px-1.5 py-0.5 rounded border border-black dark:border-white font-bold">
                  PRIMARY
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-mono truncate">{website.domain}</span>
            </div>
          </div>
        </DropdownItem>
      ))}

      <DropdownSeparator />

      <DropdownItem
        onSelect={onAddWebsite}
        disabled={websites.length >= 2}
        title={
          websites.length >= 2
            ? t('domainSwitcher.limitReached', 'Website limit reached (2/2 free plan)')
            : undefined
        }
      >
        <span className="font-medium">+ {t('domainSwitcher.add', 'Add website')}</span>
        {websites.length >= 2 && (
          <span className="text-xs text-amber-600 dark:text-amber-400 ml-2">
            (limit: 2/2)
          </span>
        )}
      </DropdownItem>
    </div>
  );
}
